/**
 * Next Steps Tab
 *
 * Full-width consolidated AI-generated action plan synthesized from
 * the performance, fatigue, and insight reports.
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { analysisApi } from './api'
import type { NextStepsResponse } from './types'

interface NextStepsTabProps {
  startDate: string
  endDate: string
}

export function NextStepsTab({ startDate, endDate }: NextStepsTabProps) {
  const [data, setData] = useState<NextStepsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async (regenerate = false) => {
    if (regenerate) setRegenerating(true)
    else setLoading(true)
    setError(null)
    try {
      const report = await analysisApi.getNextStepsReport(startDate, endDate, regenerate)
      setData(report)
    } catch (err) {
      console.error('Error fetching next steps report:', err)
      setError('Failed to load next steps report')
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchReport(false) }, [fetchReport])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Icons.Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-sm text-tf-text-muted">Building consolidated action plan...</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Icons.AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-red-600">{error || 'No data available'}</p>
        <p className="text-xs text-tf-text-muted">Tip: Generate the Performance and Fatigue reports first for better results.</p>
        <button onClick={() => fetchReport(false)} className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Cache + Regenerate */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-tf-text-muted">
          {data.from_cache ? (
            <><Icons.Database className="w-3.5 h-3.5" /><span>Cached from {new Date(data.generated_at).toLocaleString()}</span></>
          ) : (
            <><Icons.Sparkles className="w-3.5 h-3.5 text-primary" /><span>Generated {new Date(data.generated_at).toLocaleString()}</span></>
          )}
        </div>
        <button onClick={() => fetchReport(true)} disabled={regenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/15 disabled:opacity-50">
          {regenerating ? <><Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />Re-analyzing...</> : <><Icons.RefreshCw className="w-3.5 h-3.5" />Regenerate</>}
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-tf-feedback-info-bg border border-blue-200 rounded-xl">
        <Icons.Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Consolidated Action Plan</p>
          <p className="text-xs mt-0.5">This report synthesizes insights from your Performance, Fatigue, and Creative Insight analyses into a prioritized action plan.</p>
        </div>
      </div>

      {/* Full-width content */}
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6 prose prose-sm prose-gray max-w-none min-h-[400px]">
        <ReactMarkdown>{data.body || 'No analysis available. Generate the Performance and Fatigue reports first.'}</ReactMarkdown>
      </div>
    </div>
  )
}
